import {
    Component,
    ElementRef,
    HostBinding,
    HostListener,
    NgZone,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ScrollStrategy, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as jwt_decode from 'jwt-decode';

import { ChatService } from 'app/modules/admin/chat/chat.service';
import {
    ChatMessage,
    ChatUser,
    Conversation,
} from 'app/modules/admin/chat/chat.types';
import { AuthService } from 'app/core/auth/auth.service';

/**
 * Quick chat panel — Fuse template style sidebar that lives in the page chrome.
 * Shows a vertical avatar strip on the right. Clicking an avatar opens the panel
 * and selects that conversation. Powered by the real ChatService (SignalR + REST).
 */
@Component({
    selector: 'quick-chat',
    templateUrl: './quick-chat.component.html',
    styleUrls: ['./quick-chat.component.scss'],
    encapsulation: ViewEncapsulation.None,
    exportAs: 'quickChat',
})
export class QuickChatComponent implements OnInit, OnDestroy {
    @ViewChild('messageInput') messageInput!: ElementRef<HTMLTextAreaElement>;

    chats: Conversation[] = [];
    users: ChatUser[] = [];
    selectedChat: Conversation | null = null;
    messages: ChatMessage[] = [];
    opened = false;
    draft = '';
    currentUserId = 0;

    /** quick-chat search term — filters users + groups to start a chat fast. */
    searchTerm = '';

    // ----- Emoji picker -----
    showEmojiPicker = false;
    emojis: string[] = [
        '😀','😃','😄','😁','😆','😅','😂','🤣','😊','😇','🙂','🙃','😉','😍','🥰',
        '😘','😗','😚','😋','😛','😜','🤪','😝','🤔','🤨','😐','😶','😏','😒','🙄',
        '😴','😪','🥱','😎','🤓','🧐','😕','😟','🙁','😮','😢','😭','😤','😡','🤬',
        '👍','👎','👌','👏','🙏','🤝','💪','✌️','🤞','🤟','🤘','👋','🖐️','🤚','✋',
        '❤️','🧡','💛','💚','💙','💜','🖤','🤍','💔','💕','💖','💗','💘','💝','💟',
        '🎉','🎊','🎁','🎈','🎂','✨','⭐','🌟','💫','🔥','💯','✅','❌','❓','❗',
        '☕','🍕','🍔','🍰','🍩','🍿','🍪','🍫','🍦','🥤','🍺','🍷','🥂','🍻','🥃',
    ];

    private _scrollStrategy: ScrollStrategy = this._scrollStrategyOptions.block();
    private _overlay: HTMLElement | null = null;
    private _destroy$ = new Subject<void>();

    constructor(
        private _elementRef: ElementRef,
        private _renderer2: Renderer2,
        private _ngZone: NgZone,
        private _chat: ChatService,
        private _auth: AuthService,
        private _scrollStrategyOptions: ScrollStrategyOptions
    ) {}

    @HostBinding('class') get classList(): any {
        return { 'quick-chat-opened': this.opened };
    }

    /** Close the emoji picker when clicking outside its trigger / panel. */
    @HostListener('document:click', ['$event'])
    onDocClick(ev: Event): void {
        if (!this.showEmojiPicker) return;
        const target = ev.target as HTMLElement | null;
        if (!target) return;
        if (!target.closest('.emoji-picker') && !target.closest('.emoji-toggle')) {
            this.showEmojiPicker = false;
        }
    }

    toggleEmojiPicker(ev?: Event): void {
        ev?.stopPropagation();
        this.showEmojiPicker = !this.showEmojiPicker;
    }

    insertEmoji(emoji: string): void {
        this.draft = (this.draft || '') + emoji;
    }

    @HostListener('input')
    @HostListener('ngModelChange')
    private _resizeMessageInput(): void {
        if (!this.messageInput) return;
        this._ngZone.runOutsideAngular(() => {
            setTimeout(() => {
                this.messageInput.nativeElement.style.height = 'auto';
                this.messageInput.nativeElement.style.height =
                    `${this.messageInput.nativeElement.scrollHeight}px`;
            });
        });
    }

    ngOnInit(): void {
        // Resolve current user id from JWT
        try {
            const t = this._auth.accessToken;
            if (t) {
                const d: any = jwt_decode.jwtDecode(t);
                this.currentUserId = +(
                    d?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']
                    || d?.nameid || d?.sub || 0
                );
            }
        } catch { /* ignore */ }

        // Initial data + SignalR (idempotent — chat module also calls these)
        forkJoin([
            this._chat.loadConversations(),
            this._chat.loadUsers(),
        ]).subscribe();

        this._chat.start(() => this._auth.accessToken);

        this._chat.conversations$
            .pipe(takeUntil(this._destroy$))
            .subscribe((list) => {
                this.chats = list || [];
                if (this.selectedChat) {
                    const fresh = this.chats.find((c) => c.id === this.selectedChat!.id);
                    if (fresh) this.selectedChat = fresh;
                }
            });

        this._chat.users$
            .pipe(takeUntil(this._destroy$))
            .subscribe((list) => {
                // Sort: online users first, then alphabetical
                this.users = (list || []).slice().sort((a, b) => {
                    if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;
                    return (a.name || '').localeCompare(b.name || '');
                });
            });

        this._chat.activeConversation$
            .pipe(takeUntil(this._destroy$))
            .subscribe((c) => (this.selectedChat = c));

        this._chat.messages$
            .pipe(takeUntil(this._destroy$))
            .subscribe((m) => (this.messages = m || []));
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    isMine(m: ChatMessage): boolean {
        return +m.senderId === +this.currentUserId;
    }

    trackByFn(_i: number, item: any): any {
        return item?.id ?? _i;
    }

    open(): void {
        if (this.opened) return;
        this._toggleOpened(true);
    }

    close(): void {
        if (!this.opened) return;
        this._toggleOpened(false);
    }

    toggle(): void {
        this.opened ? this.close() : this.open();
    }

    /** Click an avatar in the left strip -> open & select. */
    selectChat(id: number): void {
        this._toggleOpened(true);
        this._chat.selectById(id);
    }

    /** Click a user avatar -> open the panel and start (or resume) a 1-1 chat. */
    openUser(u: ChatUser): void {
        this._toggleOpened(true);
        this._chat.getOrCreateDirect(u.id).subscribe((res: any) => {
            const id = res?.id ?? res?.result?.id;
            if (!id) return;
            this._chat.loadConversations().subscribe(() => {
                this._chat.selectById(id);
            });
        });
    }

    /** Whether the given user already has an active conversation selected. */
    isUserActive(u: ChatUser): boolean {
        return !!this.selectedChat && !this.selectedChat.isGroup
            && this.selectedChat.otherUserId === u.id;
    }

    /** Unread count for the DM with this user (used to render a badge). */
    unreadForUser(u: ChatUser): number {
        const dm = (this.chats || []).find(
            (c) => !c.isGroup && c.otherUserId === u.id
        );
        return dm?.unreadCount || 0;
    }

    hasGroups(): boolean {
        return (this.chats || []).some((c) => c.isGroup);
    }

    /** True while the user is typing in the search box. Drives the result list. */
    isSearching(): boolean {
        return !!(this.searchTerm || '').trim();
    }

    /** Users matching the search term. */
    searchUsers(): ChatUser[] {
        const q = (this.searchTerm || '').trim().toLowerCase();
        if (!q) return [];
        return this.users.filter((u) =>
            (u.name || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q) ||
            (u.userName || '').toLowerCase().includes(q)
        );
    }

    /** Group conversations matching the search term. */
    searchGroups(): Conversation[] {
        const q = (this.searchTerm || '').trim().toLowerCase();
        if (!q) return [];
        return this.chats.filter(
            (c) => c.isGroup && (c.name || '').toLowerCase().includes(q)
        );
    }

    clearSearch(): void {
        this.searchTerm = '';
    }

    /** Pick a result → open the chat and clear the search. */
    pickUser(u: ChatUser): void {
        this.openUser(u);
        this.clearSearch();
    }
    pickGroup(c: Conversation): void {
        this.selectChat(c.id);
        this.clearSearch();
    }

    onTyping(): void {
        if (this.selectedChat) this._chat.typing(this.selectedChat.id);
    }

    onEnter(ev: Event): void {
        const ke = ev as KeyboardEvent;
        if (ke.shiftKey) return;
        ev.preventDefault();
        this.send();
    }

    send(): void {
        const text = (this.draft || '').trim();
        if (!text || !this.selectedChat) return;
        this._chat
            .sendMessage(this.selectedChat.id, text)
            .then(() => {
                this.draft = '';
                if (this.messageInput) {
                    this.messageInput.nativeElement.style.height = 'auto';
                }
            })
            .catch((err) => console.error('quick-chat send failed', err));
    }

    private _showOverlay(): void {
        this._hideOverlay();
        this._overlay = this._renderer2.createElement('div');
        if (!this._overlay) return;
        this._overlay.classList.add('quick-chat-overlay');
        this._renderer2.appendChild(
            this._elementRef.nativeElement.parentElement,
            this._overlay
        );
        this._scrollStrategy.enable();
        this._overlay.addEventListener('click', () => this.close());
    }

    private _hideOverlay(): void {
        if (!this._overlay) return;
        this._overlay.parentNode?.removeChild(this._overlay);
        this._overlay = null;
        this._scrollStrategy.disable();
    }

    private _toggleOpened(open: boolean): void {
        this.opened = open;
        if (open) this._showOverlay();
        else this._hideOverlay();
    }
}
